import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import './CoinPrice.css';
import api from '../../api/axios';

const CoinPrice = () => {
  const [price, setPrice] = useState('');
  const [referralReward, setReferralReward] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [currentReferralReward, setCurrentReferralReward] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  /* ================= HISTORY STATE ================= */
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchCurrentPrice();
    fetchHistory(page);
  }, [page]);

  const fetchCurrentPrice = async () => {
    try {
      const res = await api.get('/coin-price');
      setCurrentPrice(res.data?.coinPrice?.price ?? null);
      setCurrentReferralReward(res.data?.coinPrice?.referralReward ?? null);
    } catch (error) {
      console.error('Fetch error:', error);
      setCurrentPrice(null);
      setCurrentReferralReward(null);
    }
  };

  const fetchHistory = async (pageNo) => {
    try {
      setHistoryLoading(true);
      const res = await api.get('/admin/history/coin-price', {
        params: { page: pageNo, limit }
      });

      if (res.data.success) {
        setHistory(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch (error) {
      console.error('History fetch error:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!price || Number(price) <= 0) {
      setMessage('Please enter a valid coin price');
      return;
    }

    if (referralReward !== '' && Number(referralReward) < 0) {
      setMessage('Referral reward cannot be negative');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const payload = { price: Number(price) };
      if (referralReward !== '') payload.referralReward = Number(referralReward);

      await api.post('/admin/coin-price', payload);
      toast.success('Coin configuration saved successfully');

      setCurrentPrice(Number(price));
      if (referralReward !== '') setCurrentReferralReward(Number(referralReward));
      setPrice('');
      setReferralReward('');
      fetchHistory(1);
      setPage(1);

      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error:', error);
      setMessage(error.response?.data?.message || 'Error saving coin configuration');
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= DATE FORMATTER ================= */
  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="coin-price">
      <div className="coin-price-header">
        <h2>Coin Price Management</h2>
      </div>

      <div className="coin-price-content">
        <div className="current-price-section">
          <h3>Current Configuration</h3>
          <div className="current-price-display">
            ₹{currentPrice ?? 1} <span style={{ fontSize: '14px', color: '#666' }}>per coin</span>
          </div>
          <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '15px', color: '#4f46e5', fontWeight: 600 }}>
            Referral Reward: {currentReferralReward ?? 50} coins
          </div>
        </div>

        <div className="update-price-section">
          <h3>Update Configuration</h3>
          <form onSubmit={handleSubmit} className="price-form">
            <div className="input-group">
              <label htmlFor="price">Coin Value (₹ per coin)</label>
              <input
                type="number"
                id="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 10"
                min="1"
                step="0.01"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="referralReward">Referral Reward (coins)</label>
              <input
                type="number"
                id="referralReward"
                value={referralReward}
                onChange={(e) => setReferralReward(e.target.value)}
                placeholder="e.g. 100"
                min="0"
                step="1"
              />
            </div>

            <button type="submit" className="update-btn" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Configuration'}
            </button>
          </form>

          {message && (
            <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
              {message}
            </div>
          )}
        </div>
      </div>

      {/* ================= HISTORY TABLE (NEW) ================= */}
      <div className="coin-history-section">
        <h3>Coin Price History</h3>

        <div className="table-container">
          <table className="coin-history-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Coin Value (₹)</th>
                <th>Referral Reward</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan="3" align="center">Loading...</td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan="3" align="center">No history found</td>
                </tr>
              ) : (
                history.map((item, index) => (
                  <tr key={item._id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>₹{item.price}</td>
                    <td>{item.referralReward ?? 50} coins</td>
                    <td>{formatDateTime(item.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Prev
          </button>
          <span>
            {page} / {pagination.totalPages || 1}
          </span>
          <button
            disabled={page === pagination.totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
       <ToastContainer
              toastClassName="custom-toast"
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
    </div>
  );
};

export default CoinPrice;
